const DB_NAME = 'atten-newline';
const DB_VERSION = 1;

const STORES = {
  SESSION: 'session',
  FEED_KEYS: 'feed_keys'
} as const;

let db: IDBDatabase | null = null;

async function getDB(): Promise<IDBDatabase> {
  if (db) return db;
  
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };
    
    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;
      
      if (!database.objectStoreNames.contains(STORES.SESSION)) {
        database.createObjectStore(STORES.SESSION, { keyPath: 'id' });
      }
      
      if (!database.objectStoreNames.contains(STORES.FEED_KEYS)) {
        database.createObjectStore(STORES.FEED_KEYS, { keyPath: 'version' });
      }
    };
  });
}

export interface SessionData {
  id: string;
  userId: string;
  scratchUsername: string;
  encryptedSk: string;
  salt: string;
  sessionKey: string;
  createdAt: string;
}

export interface FeedKeyData {
  version: string;
  keyData: string;
  createdAt: string;
}

export async function saveSession(session: SessionData): Promise<void> {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORES.SESSION, 'readwrite');
    const store = transaction.objectStore(STORES.SESSION);
    const request = store.put(session);
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getSession(): Promise<SessionData | null> {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORES.SESSION, 'readonly');
    const store = transaction.objectStore(STORES.SESSION);
    const request = store.get('current');
    
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

export async function deleteSession(): Promise<void> {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORES.SESSION, 'readwrite');
    const store = transaction.objectStore(STORES.SESSION);
    const request = store.delete('current');
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function saveFeedKey(feedKey: FeedKeyData): Promise<void> {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORES.FEED_KEYS, 'readwrite');
    const store = transaction.objectStore(STORES.FEED_KEYS);
    const request = store.put(feedKey);
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getFeedKey(version: string): Promise<FeedKeyData | null> {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORES.FEED_KEYS, 'readonly');
    const store = transaction.objectStore(STORES.FEED_KEYS);
    const request = store.get(version);
    
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllFeedKeys(): Promise<FeedKeyData[]> {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORES.FEED_KEYS, 'readonly');
    const store = transaction.objectStore(STORES.FEED_KEYS);
    const request = store.getAll();
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function deleteFeedKeys(): Promise<void> {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORES.FEED_KEYS, 'readwrite');
    const store = transaction.objectStore(STORES.FEED_KEYS);
    const request = store.clear();
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}